import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { BarChart3, Loader2 } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { useNoIndex } from "@/lib/noIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const monthLabel = (ym) => `${MONTHS[+ym.slice(5, 7) - 1]} ${ym.slice(2, 4)}`;

const Totals = ({ s }) => (
  <div className="mt-10 grid grid-cols-2 gap-px bg-white/10" data-testid="stats-totals">
    {[["Totale PDF scaricati", s.total, "total"], ["Ultimi 30 giorni", s.last30, "last30"]].map(([l, v, id]) => (
      <div key={id} className="bg-[#121110] p-6">
        <p className="font-display text-4xl font-bold text-[#D98A52] tabular-nums" data-testid={`stats-${id}`}>{v}</p>
        <p className="mt-1 text-sm text-[#D6D0C4]">{l}</p>
      </div>
    ))}
  </div>
);

const Chips = ({ items, id }) => (
  <ul className="mt-3 flex flex-wrap gap-2" data-testid={id}>
    {items.length ? items.map((x) => (
      <li key={x.label} className="rounded-full border border-white/15 px-3 py-1 text-sm"><b className="tabular-nums">{x.label}</b> <span className="text-[#D6D0C4]">· {x.count}</span></li>
    )) : <li className="text-sm text-[#A39E93]">Nessun dato ancora</li>}
  </ul>
);

const Tops = ({ spans, grades, id }) => (
  <div className="mt-6 grid gap-6 sm:grid-cols-2">
    <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">Luci più calcolate</p><Chips items={Array.isArray(spans) ? spans : []} id={`${id}-spans`} /></div>
    {grades && <div><p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D98A52]">Classi più calcolate</p><Chips items={grades} id={`${id}-grades`} /></div>}
  </div>
);

const Row = ({ c, max }) => (
  <div className="border-t border-white/10 py-8" data-testid={`stats-row-${c.mode}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <h2 className="font-display text-2xl font-bold">{c.name}</h2>
      <p className="text-sm text-[#D6D0C4]"><b className="text-[#F3EFE6] tabular-nums" data-testid={`stats-row-${c.mode}-total`}>{c.total}</b> totali · <b className="text-[#F3EFE6] tabular-nums">{c.last30}</b> negli ultimi 30 giorni</p>
    </div>
    <div className="mt-6 grid grid-cols-12 items-end gap-1 sm:gap-2" aria-label={`Andamento mensile ${c.name}`}>
      {c.months.map((m) => (
        <div key={m.month} className="flex flex-col items-center gap-1">
          <span className="text-xs tabular-nums text-[#D6D0C4]">{m.count || ""}</span>
          <div className="h-24 w-full bg-white/5"><div className="mt-auto h-full w-full origin-bottom bg-[#D98A52] transition-transform duration-700" style={{ transform: `scaleY(${max ? m.count / max : 0})` }} /></div>
          <span className="text-[10px] uppercase text-[#A39E93] sm:text-xs">{monthLabel(m.month)}</span>
        </div>
      ))}
    </div>
    <Tops spans={c.spans} grades={c.mode === "xlam" ? null : c.grades ?? []} id={`stats-row-${c.mode}`} />
  </div>
);

export default function CalcStats() {
  const { token } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  useNoIndex();
  useEffect(() => {
    axios.get(`${API}/calc-stats/${token}`).then((r) => setS(r.data)).catch(() => setErr(true));
  }, [token]);
  const calcs = Array.isArray(s?.calcs) ? s.calcs : [];
  const max = Math.max(0, ...calcs.flatMap((c) => c.months.map((m) => m.count)));
  return (
    <div className="min-h-screen bg-[#121110] text-[#F3EFE6]" data-testid="stats-page">
      <Nav />
      <main className="mx-auto max-w-4xl px-5 pb-28 pt-40 sm:px-8">
        <BarChart3 className="h-9 w-9 text-[#D98A52]" />
        <h1 className="mt-6 font-display text-4xl font-bold tracking-tight sm:text-5xl">PDF di calcolo scaricati</h1>
        <p className="mt-6 text-base leading-relaxed text-[#D6D0C4]">Quante volte è stato scaricato il calcolo in PDF da ogni calcolatore del sito, con l'andamento degli ultimi 12 mesi.</p>
        {err && <p className="mt-10 text-[#E9A06B]" data-testid="stats-error">Link scaduto o non valido: richiedine uno nuovo dall'Area riservata.</p>}
        {!s && !err && <Loader2 className="mt-10 h-6 w-6 animate-spin text-[#D98A52]" data-testid="stats-loading" />}
        {s && <>
          <Totals s={s} />
          <section className="mt-10 border border-white/10 p-6" data-testid="stats-tops">
            <h2 className="font-display text-2xl font-bold">Cosa chiedono i progettisti</h2>
            <p className="mt-2 text-sm text-[#D6D0C4]">Luci (arrotondate al mezzo metro) e classi di resistenza dei calcoli scaricati, su tutti i calcolatori.</p>
            <Tops spans={s.spans} grades={Array.isArray(s.grades) ? s.grades : []} id="stats-all" />
          </section>
          <div className="mt-10">{calcs.map((c) => <Row key={c.mode} c={c} max={max} />)}</div>
        </>}
      </main>
      <Footer />
    </div>
  );
}
